import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getBrowserTimezone } from '../utils/dateUtils';
import AuthSplitLayout from '../components/AuthSplitLayout';
import authSignupBotanical from '../assets/auth-signup-botanical.png';

const steps = ['Profile', 'Security', 'Health'];

export default function SignUpPageEnhanced() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signup } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    age: '',
    weight: '',
    height: '',
    gender: 'male',
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailFromQuery = params.get('email');

    if (!emailFromQuery) {
      return;
    }

    setFormData((current) => (
      current.email
        ? current
        : {
            ...current,
            email: emailFromQuery,
          }
    ));
  }, [location.search]);

  const handleChange = (event) => {
    setFormData((current) => ({ ...current, [event.target.name]: event.target.value }));
    setError('');
  };

  const validateStep = () => {
    if (activeStep === 0) {
      if (!formData.name.trim()) return 'Name is required';
      if (!/\S+@\S+\.\S+/.test(formData.email)) return 'Enter a valid email address';
    }
    if (activeStep === 1) {
      if (formData.password.length < 8) return 'Password must be at least 8 characters';
      if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
    }
    if (activeStep === 2) {
      if (!formData.age || Number(formData.age) < 1 || Number(formData.age) > 150) return 'Enter a valid age';
      if (!formData.weight || Number(formData.weight) < 1) return 'Enter a valid weight';
      if (!formData.height || Number(formData.height) < 1) return 'Enter a valid height';
    }
    return '';
  };

  const handleNext = () => {
    const message = validateStep();
    if (message) {
      setError(message);
      return;
    }
    setActiveStep((current) => Math.min(current + 1, steps.length - 1));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const message = validateStep();
    if (message) {
      setError(message);
      return;
    }

    setLoading(true);
    try {
      await signup({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        age: parseInt(formData.age, 10),
        weight: parseFloat(formData.weight),
        height: parseFloat(formData.height),
        gender: formData.gender,
        timezone: getBrowserTimezone(),
      });
      navigate('/onboarding');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitLayout
      leftImage={authSignupBotanical}
      leftImageMood="bright"
      title={
        <>
          Build a healthier
          <br />
          rhythm.
        </>
      }
      subtitle="Create your FastTime account to start tracking fasts, meals, energy, and the habits that support real progress."
      rightTitle="Get started"
      rightSubtitle="Create your FastTime account"
      footer={
        <Typography variant="body2" sx={{ color: 'rgba(31,43,34,0.62)', textAlign: 'center' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#b46f47', fontWeight: 600, textDecoration: 'none' }}>
            Log in
          </Link>
        </Typography>
      }
    >
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} sx={{ color: '#1f2b22', px: 0 }}>
          Back
        </Button>
      </Box>

      <Box sx={{ mb: 3.5 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
          {steps.map((step, index) => (
            <Typography
              key={step}
              variant="body2"
              sx={{
                color: index === activeStep ? '#1f2b22' : 'rgba(31,43,34,0.45)',
                fontWeight: index === activeStep ? 600 : 500,
              }}
            >
              {step}
            </Typography>
          ))}
        </Stack>
        <LinearProgress
          variant="determinate"
          value={((activeStep + 1) / steps.length) * 100}
          sx={{
            height: 8,
            bgcolor: 'rgba(31,43,34,0.08)',
            '& .MuiLinearProgress-bar': { backgroundColor: '#1f2b22' },
          }}
        />
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(230,124,115,0.08)', color: '#7b2f28' }}>
          {error}
        </Alert>
      ) : null}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          {activeStep === 0 && (
            <>
              <TextField
                fullWidth
                label="Full name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                autoFocus
              />
              <TextField
                fullWidth
                label="Email address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </>
          )}

          {activeStep === 1 && (
            <>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                helperText="Use at least 8 characters"
                autoFocus
              />
              <TextField
                fullWidth
                label="Confirm password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
            </>
          )}

          {activeStep === 2 && (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Age"
                  name="age"
                  type="number"
                  value={formData.age}
                  onChange={handleChange}
                  autoFocus
                />
                <TextField
                  fullWidth
                  select
                  label="Gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                </TextField>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Weight (kg)"
                  name="weight"
                  type="number"
                  value={formData.weight}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  label="Height (cm)"
                  name="height"
                  type="number"
                  value={formData.height}
                  onChange={handleChange}
                />
              </Stack>
            </>
          )}
        </Stack>

        <Stack direction="row" spacing={1.5} sx={{ mt: 4 }}>
          {activeStep > 0 && (
            <Button fullWidth variant="outlined" onClick={() => setActiveStep((current) => current - 1)}>
              Back
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button
              fullWidth
              variant="contained"
              type="button"
              onClick={handleNext}
              sx={{ bgcolor: '#122019', color: '#f5efe4', '&:hover': { bgcolor: '#1a2d24' } }}
            >
              Continue
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading}
              sx={{ bgcolor: '#122019', color: '#f5efe4', '&:hover': { bgcolor: '#1a2d24' } }}
            >
              {loading ? 'Creating account…' : 'Create account'}
            </Button>
          )}
        </Stack>
      </Box>
    </AuthSplitLayout>
  );
}
